/**
 * Server-render data snapshot.
 *
 * server.js inlines the API payload a page needs into a
 * <script id="__SNAPSHOT__" type="application/json"> tag. Contexts read their *initial* state
 * from here so the server-rendered markup and the first client render agree — without it the
 * app would mount with empty arrays and visibly repaint every page.
 *
 * The snapshot is a starting point, never the truth: every context still fetches on mount and
 * replaces whatever was seeded, so users always end up on live data even if the server's cache
 * was a few seconds stale.
 *
 * Absent keys mean "not seeded" — callers fall back to their normal empty state.
 */

const SNAPSHOT_ELEMENT_ID = "__SNAPSHOT__";

let snapshot = null;

/** Server-side entry point: seed the snapshot for the page about to be rendered. */
export function setSnapshot(value) {
  snapshot = value || {};
}

function readFromDocument() {
  if (typeof document === "undefined") return {};

  const element = document.getElementById(SNAPSHOT_ELEMENT_ID);
  if (!element) return {};

  try {
    const parsed = JSON.parse(element.textContent);
    // The data is now in memory; drop the tag so it isn't serialized twice by anything else.
    element.remove();
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** The whole snapshot. Parsed from the DOM exactly once. */
export function getSnapshot() {
  if (snapshot === null) {
    snapshot = readFromDocument();
  }
  return snapshot;
}

/**
 * One seeded value, or `fallback` when this page didn't inline it.
 * @template T
 * @param {string} key
 * @param {T} fallback
 * @returns {T}
 */
export function getSnapshotValue(key, fallback) {
  const value = getSnapshot()[key];
  return value === undefined ? fallback : value;
}

/** True when the page inlined this dataset — lets contexts start in a non-loading state. */
export function hasSnapshotValue(key) {
  return getSnapshot()[key] !== undefined;
}
